import { FC } from 'react';
import { motion } from 'framer-motion';

import mintScreenshotTall from '../../../assets/minting_fxrp_screenshot_tall.png';
import redeemScreenshot from '../../../assets/redeem_fxrp_screenshot.PNG';
import faceIdClip from '../../../assets/face_id_clip.jpg';
import appStoreBadge from '../../../assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import { APP_STORE_URL } from '../../../constants/links';

const AUREUS = {
  green: '#6FC2AE',
  blue: '#609EBD',
  navy: '#01263D',
  purple: '#4643D3'
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 }
  }
};

/**
 * Static product walkthrough — marketing only, no wallet/web functionality.
 */
const HowItWorksSection: FC = () => {
  return (
    <section
      id="how-it-works"
      className="py-24 relative overflow-hidden"
      style={{ background: 'rgba(1, 38, 61, 0.25)' }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 right-0 w-80 h-80 rounded-full blur-3xl opacity-20"
          style={{ background: AUREUS.purple }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl px-6">
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <p
            className="text-xs uppercase tracking-[0.25em] mb-4 font-medium"
            style={{ color: AUREUS.blue }}
          >
            How it works
          </p>
          <h2 className="text-4xl md:text-5xl font-black mb-5">
            From install to{' '}
            <span style={{ color: AUREUS.green }}>yield</span>
          </h2>
          <p className="text-xl opacity-80 max-w-2xl mx-auto">
            Everything happens in the iOS app — this site is just the front door.
          </p>
        </motion.div>

        {/* Three steps */}
        <motion.ol
          className="grid md:grid-cols-3 gap-8 mb-20 list-none p-0 m-0"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.li
            className="rounded-2xl border p-8"
            variants={fadeInUp}
            style={{
              background: 'rgba(1, 38, 61, 0.45)',
              borderColor: 'rgba(96, 158, 189, 0.18)'
            }}
          >
            <span
              className="text-sm font-mono font-semibold"
              style={{ color: AUREUS.green }}
            >
              01
            </span>
            <h3 className="text-2xl font-bold mt-3 mb-3">Download on iOS</h3>
            <p className="opacity-80 mb-6 leading-relaxed">
              Get Oxen Flow from the App Store. No seed phrase setup —
              registration is built around passkeys.
            </p>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex transition-transform hover:scale-[1.03]"
              aria-label="Download Oxen Flow on the App Store"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="h-12 w-auto"
              />
            </a>
          </motion.li>

          <motion.li
            className="rounded-2xl border p-8"
            variants={fadeInUp}
            style={{
              background: 'rgba(1, 38, 61, 0.45)',
              borderColor: 'rgba(96, 158, 189, 0.18)'
            }}
          >
            <span
              className="text-sm font-mono font-semibold"
              style={{ color: AUREUS.green }}
            >
              02
            </span>
            <h3 className="text-2xl font-bold mt-3 mb-3">
              Sign in with a passkey
            </h3>
            <p className="opacity-80 mb-6 leading-relaxed">
              Face ID, Touch ID, or a security key. You control the keys — Oxen
              Flow stays non-custodial.
            </p>
            <div className="rounded-xl overflow-hidden border border-slate-700/40 max-h-40">
              <img
                src={faceIdClip}
                alt="Oxen Flow passkey login with Face ID"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.li>

          <motion.li
            className="rounded-2xl border p-8"
            variants={fadeInUp}
            style={{
              background: 'rgba(1, 38, 61, 0.45)',
              borderColor: 'rgba(96, 158, 189, 0.18)'
            }}
          >
            <span
              className="text-sm font-mono font-semibold"
              style={{ color: AUREUS.green }}
            >
              03
            </span>
            <h3 className="text-2xl font-bold mt-3 mb-3">Mint FXRP &amp; earn</h3>
            <p className="opacity-80 leading-relaxed">
              Bridge XRP to FXRP in-app, access yield on Flare, and redeem back
              to XRP anytime — no external dapp hopping.
            </p>
          </motion.li>
        </motion.ol>

        {/* Mint + redeem product shots */}
        <motion.div
          className="text-center mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-2">
            Mint &amp; redeem, one tap
          </h3>
          <p className="opacity-75 max-w-xl mx-auto">
            Real app screens — FXRP bridge flows live inside Oxen Flow.
          </p>
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 gap-10 max-w-3xl mx-auto items-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.figure variants={fadeInUp} className="text-center m-0">
            <div className="rounded-[2rem] overflow-hidden border border-slate-700/40 shadow-2xl mx-auto max-w-[240px] bg-black/20">
              <img
                src={mintScreenshotTall}
                alt="Mint FXRP from XRP in the Oxen Flow iOS app"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <figcaption className="mt-4 text-sm font-medium opacity-80">
              Mint FXRP
            </figcaption>
          </motion.figure>
          <motion.figure variants={fadeInUp} className="text-center m-0">
            <div className="rounded-[2rem] overflow-hidden border border-slate-700/40 shadow-2xl mx-auto max-w-[240px] bg-black/20">
              <img
                src={redeemScreenshot}
                alt="Redeem FXRP back to XRP in the Oxen Flow iOS app"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <figcaption className="mt-4 text-sm font-medium opacity-80">
              Redeem to XRP
            </figcaption>
          </motion.figure>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
